/**
 * 로그아웃 관련 유틸리티 함수들
 * Spring Authorization Server 표준 OIDC RP-Initiated Logout 지원
 */

import type { ITokenManager } from '@edusense/shared-types-auth';
import type { IIdeMessagingService } from '@edusense/shared-types-ide';
import type { AuthState } from '@edusense/shared-types-auth';
import { getValidIdToken } from './token-validation-utils';
import { performFullCleanup } from './cleanup-utils';
import { performOidcLogout } from './oidc-logout-utils';
import { clearAuthCookies } from './cookie-utils';
import type { ResolvedStorageKeys } from '../storage-keys';

/**
 * WebAuthProvider용 간단한 로그아웃 함수
 * authserver 세션을 정리하기 위해 가능한 경우 id_token_hint를 붙이고, 없더라도 logout endpoint를 호출한다.
 */
export const performWebLogout = async (
  navigate: (path: string) => void,
  authConfig?: any,
  storageKeys?: Partial<ResolvedStorageKeys>,
): Promise<void> => {
  console.log('[AuthUtils] 🚪 WebAuthProvider 로그아웃 시작');
  
  // localStorage 상태 디버깅
  const rawIdToken = localStorage.getItem(storageKeys?.idTokenKey || 'auth_id_token');
  console.log('[AuthUtils] 🔍 Raw ID Token 존재 여부:', !!rawIdToken);
  if (rawIdToken) {
    console.log('[AuthUtils] 🔍 Raw ID Token 앞 20자:', rawIdToken.substring(0, 20) + '...');
  }
  
  // 모든 auth 관련 localStorage 키 확인
  const allAuthKeys = Object.keys(localStorage).filter(key => key.startsWith('auth_'));
  console.log('[AuthUtils] 🔍 모든 auth 키들:', allAuthKeys);
  
  const idToken = getValidIdToken(storageKeys);
  console.log('[AuthUtils] 🔍 유효한 ID 토큰 존재 여부:', !!idToken);
  
  console.log('[AuthUtils] ✅ 로컬 정리 후 OIDC 로그아웃 호출');
  performFullCleanup(storageKeys);

  console.log('[AuthUtils] 🔄 performOidcLogout 호출 시작');
  performOidcLogout(navigate, authConfig, idToken);
  console.log('[AuthUtils] 🔄 performOidcLogout 호출 완료');
};

interface LogoutConfig {
  tokenManager: ITokenManager;
  apiService: any;
  isVSCodeEnv: boolean;
  ideMessengerInstance?: IIdeMessagingService | null;
  navigate: (path: string) => void;
  setAuthState: (state: AuthState | ((prev: AuthState) => AuthState)) => void;
  lastTokenRef: React.MutableRefObject<string | null>;
  authConfig?: any;
  storageKeys?: Partial<ResolvedStorageKeys>;
}

/**
 * React 상태 초기화 헬퍼 함수
 */
const initializeAuthState = (config: LogoutConfig): void => {
  config.setAuthState({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
    isInitialized: true,
    error: null
  });
  config.lastTokenRef.current = null;
  config.tokenManager.setToken(null);
  config.apiService.setToken(null);
};

/**
 * VSCode 환경용 로그아웃 처리
 */
const handleVSCodeLogout = async (config: LogoutConfig): Promise<void> => {
  console.log('[AuthUtils] VSCode 환경 로그아웃 시작');
  
  // VSCode 환경에서는 세션 로그아웃 요청
  if (config.ideMessengerInstance) {
    await config.ideMessengerInstance.request('edusenseLogout', {});
  }
  
  // VSCode 환경에서도 전체 정리
  performFullCleanup(config.storageKeys);
  
  // VSCode 환경 상태 초기화
  initializeAuthState(config);
  
  console.log('[AuthUtils] VSCode 로그아웃 완료');
  config.navigate('/landing?logout=true&env=vscode');
};

/**
 * 웹 환경용 로그아웃 처리
 */
const handleWebLogout = async (config: LogoutConfig): Promise<void> => {
  console.log('[AuthUtils] 웹 환경 로그아웃 시작');
  
  const idToken = getValidIdToken(config.storageKeys);
  console.log('[AuthUtils] 로컬 정리 후 OIDC 호출', { hasIdToken: !!idToken });
  performFullCleanup(config.storageKeys);
  initializeAuthState(config);

  performOidcLogout(config.navigate, config.authConfig, idToken);
};

/**
 * 로그아웃 함수 생성
 * 
 * @param config 로그아웃 설정
 */
export const createLogoutHandler = (config: LogoutConfig) => async () => {
  console.log('🟡🟡🟡 createLogoutHandler 호출됨! 🟡🟡🟡');
  console.log('🟡🟡🟡 이 로그가 보이면 useAuthProvider가 사용되고 있습니다! 🟡🟡🟡');
  console.log('[AuthUtils] 로그아웃 시작');

  try {
    if (config.isVSCodeEnv) {
      await handleVSCodeLogout(config);
    } else {
      await handleWebLogout(config);
    }
  } catch (error) {
    console.error('[AuthUtils] 로그아웃 중 오류:', error);
    
    // 오류 발생 시에도 최소한의 상태 초기화
    initializeAuthState(config);
    
    const errorMessage = error instanceof Error ? error.message : 'logout_error';
    config.navigate(`/landing?logout=true&error=${encodeURIComponent(errorMessage)}`);
  }
};
